import apiClient from '../client';
import type {
  TaskListItem,
  TaskDetail,
  TaskStats,
  TaskFilters,
  CreateTaskRequest,
  UpdateTaskRequest,
  CompleteTaskRequest,
} from '@/types/task.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const tasksApi = {
  /**
   * Lista tarefas com filtros e paginação.
   */
  getAll: async (
    params: PaginationParams & TaskFilters
  ): Promise<PaginatedResponse<TaskListItem>> => {
    const response = await apiClient.get<PaginatedResponse<TaskListItem>>('/v1/tasks', { params });
    return response.data;
  },

  /**
   * Lista tarefas pendentes do usuário atual.
   */
  getMyPending: async (): Promise<TaskListItem[]> => {
    const response = await apiClient.get<TaskListItem[]>('/v1/tasks/my');
    return response.data;
  },

  /**
   * Lista tarefas não atribuídas.
   */
  getUnassigned: async (params: PaginationParams): Promise<PaginatedResponse<TaskListItem>> => {
    const response = await apiClient.get<PaginatedResponse<TaskListItem>>('/v1/tasks/unassigned', {
      params,
    });
    return response.data;
  },

  /**
   * Lista tarefas atrasadas.
   */
  getOverdue: async (params: PaginationParams): Promise<PaginatedResponse<TaskListItem>> => {
    const response = await apiClient.get<PaginatedResponse<TaskListItem>>('/v1/tasks/overdue', {
      params,
    });
    return response.data;
  },

  /**
   * Lista tarefas de um paciente específico.
   */
  getByPatient: async (patientId: string): Promise<TaskListItem[]> => {
    const response = await apiClient.get<TaskListItem[]>(`/v1/tasks/patient/${patientId}`);
    return response.data;
  },

  /**
   * Retorna estatísticas das tarefas.
   */
  getStats: async (): Promise<TaskStats> => {
    const response = await apiClient.get<TaskStats>('/v1/tasks/stats');
    return response.data;
  },

  /**
   * Busca tarefa por ID.
   */
  getById: async (id: string): Promise<TaskDetail> => {
    const response = await apiClient.get<TaskDetail>(`/v1/tasks/${id}`);
    return response.data;
  },

  /**
   * Cria uma nova tarefa.
   */
  create: async (data: CreateTaskRequest): Promise<TaskDetail> => {
    const response = await apiClient.post<TaskDetail>('/v1/tasks', data);
    return response.data;
  },

  /**
   * Atualiza uma tarefa existente.
   */
  update: async (id: string, data: UpdateTaskRequest): Promise<TaskDetail> => {
    const response = await apiClient.put<TaskDetail>(`/v1/tasks/${id}`, data);
    return response.data;
  },

  /**
   * Atribui uma tarefa a um colaborador.
   */
  assign: async (id: string, userId: string): Promise<TaskDetail> => {
    const response = await apiClient.post<TaskDetail>(`/v1/tasks/${id}/assign/${userId}`);
    return response.data;
  },

  /**
   * Inicia uma tarefa pendente.
   */
  start: async (id: string): Promise<TaskDetail> => {
    const response = await apiClient.post<TaskDetail>(`/v1/tasks/${id}/start`);
    return response.data;
  },

  /**
   * Marca uma tarefa como concluída.
   */
  complete: async (id: string, data: CompleteTaskRequest): Promise<TaskDetail> => {
    const response = await apiClient.post<TaskDetail>(`/v1/tasks/${id}/complete`, data);
    return response.data;
  },

  /**
   * Cancela uma tarefa.
   */
  cancel: async (id: string, reason?: string): Promise<TaskDetail> => {
    const response = await apiClient.post<TaskDetail>(`/v1/tasks/${id}/cancel`, null, {
      params: { reason },
    });
    return response.data;
  },
};
